import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AttributeResource } from '@df/catalog/attribute/attribute.resource';
import { IProductDataV2 } from '@df/catalog/product/product';
import { Status } from '@df/core/status';
import { CustomerModel } from '@df/session/api/customer.model';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { PriceComponent } from '@df/ui/price/price.component';
import { PriceService, TaxMode } from '@df/ui/price/price.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { TradeService } from '../../../checkout/module/trade/trade.service';
import { BecomeProBanner2Component } from '../../../common/become-pro-banner-2/become-pro-banner-2.component';
import { InlineButtonComponent } from '../../../common/buttons/inline-button/inline-button.component';
import { NProductTierPriceModal } from '../product-tier-price-modal/product-tier-price-modal.component';
import { ITierPrice, Product } from '../product.decorator';
import { ProductComponent } from '../product/product.component';

interface IPromoBarWasPrice {
    type: 'wasPrice';
    wasPrice: number;
    discount: number;
    percent: number;
}

interface IPromoBarBulkPrice {
    type: 'bulkPrice';
    tierPrices: ITierPrice[];
}
interface IPromoBarBecomePro {
    type: 'becomePro';
    isTradeCustomer: boolean;
}
interface IPromoBarLabel {
    type: 'label';
    label: string;
    color?: string;
}

export type TPromoBar = IPromoBarWasPrice | IPromoBarBulkPrice | IPromoBarLabel | IPromoBarBecomePro | undefined;

/**
 * Displays product price
 */
@Component({
    selector: 'product-promo-bar',
    templateUrl: './product-promo-bar.html',
    styleUrls: ['./product-promo-bar.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [BecomeProBanner2Component, TranslationModule, InlineButtonComponent, CommonModule, PriceComponent, SizeClassDirective]
})
export class ProductPromoBarComponent extends UiComponent implements OnInit, OnChanges {
    @Input({
        required: true
    })
    product!: Product<IProductDataV2>;

    @Output()
    promoBarChange = new EventEmitter<TPromoBar>();

    protected source: TPromoBar;
    protected bulkStatus = new Status();

    private customerModel = inject(CustomerModel);
    private priceService = inject(PriceService);
    private attributeResource = inject(AttributeResource);
    private tradeService = inject(TradeService);
    private dfModalService = inject(DfModalService);
    private productComponent = inject(ProductComponent);

    override ngOnInit(): void {
        const update = this.timeoutService.debounce(() => this.update(), 100);
        this.customerModel.obs.data.subscribe(update);
        this.priceService.catalogPricesInclTax$.subscribe(update);
        super.ngOnInit();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        if (changes['product']) {
            this.update();
        }
        super.ngOnChanges(changes);
    }

    protected openTierPriceModal() {
        const data: NProductTierPriceModal.IModalData = {
            product: this.product,
            onSelect: (qty: number) => {
                this.productComponent.onQtyChange(qty);
            }
        };
        this.dfModalService.open(this.bulkStatus, NProductTierPriceModal.MODAL_ID, undefined, data);
    }

    private update() {
        const promoBar = this.preparePromoBar();
        if (promoBar !== this.source) {
            this.source = promoBar;
            this.promoBarChange.emit(promoBar);
        }
        this.detectChanges();
    }

    private preparePromoBar(): TPromoBar {
        const customerGroup = this.customerModel.data.group_id!;

        const data = this.product.data as IProductDataV2;
        // was price logic
        const wasPriceValue = data.was_price;
        const wasPrice = wasPriceValue ? this.priceService.applyTax(wasPriceValue, data.tax, TaxMode.auto) : undefined;
        const price = this.product.price;
        if (wasPrice && wasPrice > price) {
            return {
                type: 'wasPrice',
                wasPrice: wasPrice,
                discount: wasPrice - price,
                percent: Math.round((100 * (wasPrice - price)) / wasPrice)
            };
        }

        const manualPromoPriceLabel = data.manual_promo_price_label;
        if (manualPromoPriceLabel) {
            const attribute = this.attributeResource.getExisting('manual_promo_price_label');
            if (attribute) {
                return {
                    type: 'label',
                    label: attribute.getOptionLabel(manualPromoPriceLabel) as string,
                    color: attribute.getOptionColor(manualPromoPriceLabel) as string | undefined
                };
            }
        }

        // bulk price offer
        if (this.product.hasTierPrices(customerGroup)) {
            return {
                type: 'bulkPrice',
                tierPrices: this.product.getTierPrices(customerGroup) as ITierPrice[]
            };
        }

        // become pro banner
        if (this.product.calcTradeDiscount()) {
            return {
                type: 'becomePro',
                isTradeCustomer: this.tradeService.isTradeCustomer()
            };
        }

        return undefined;
    }
}
